import { IPoi } from 'features/explore/model/poi-model/poi';
import {
    BillboardIcon,
    Button,
    GisIcon,
    LockIcon,
    PoiIcon,
    UtilityMoreIcon,
    VenueIcon,
    VoidIcon,
    ZipCodeIcon,
} from '@placer-ui/components';
import { PoiActionsProps } from 'features/explore/components/poi-actions/poi-actions';
import {
    useIsLockedPoi,
    useOnLockedButtonClick,
} from 'hooks/use-locked-poi-hooks/use-locked-poi-hooks';
import { useCheckCoordinateForNearbyReport } from 'features/explore/hooks/use-explore-nearby-activity';
import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { getFeatureFlags, getPermissions } from 'core/flow-control';
import { isAnEnclosingComplexFlag } from 'utils/enclosing-complexes-filter/enclosing-complexes-filter';
import { isLoadingExploreEnclosingComplexesSignal } from 'features/explore/store/explore-signals/explore-encloasing-complexes-signal';
import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useSendExploreSplunkEvent } from 'features/explore/hooks/use-explore-tracking';
import { useGetOverlayPOI } from 'features/explore/hooks/use-explore-overlay-poi';
import { useOpenExploreReportLink } from 'features/explore/hooks/use-explore-report-link';
import { getPlacePoiProvider } from 'utils/poi-provider/poi-provider';
import { ADDRESS_ENTITY, COORDINATES_ENTITY } from 'features/explore/constants';
import { getSelectedPoiOverlay } from 'features/explore/utils/utils';
import { setEnclosingNote } from 'features/insights/components/enclosing-notification-bar/utils/utils';
import {
    useSetOverlayPOI,
    useSetTriggerCreatePOI,
} from 'features/explore/context/use-explore-ui-state-actions';
import { useNearbyActivityRadiusDefault } from 'hooks/use-nearby-activity-radius/use-nearby-activity-radius';
import { createPlaceWithNearbyResult } from 'utils/create-place-with-nearby-result/create-place-with-nearby-result';
import { DropdownOption } from 'ui-components/dropdown-menu-button/dropdown-menu-button';
import { useCheckPoiForPropertyReport } from 'features/explore/hooks/use-check-for-property-report';
import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import { useGetEnclosingComplexOptions } from 'features/explore/hooks/use-explore-enclosing-complex';
import { useExploreVoidOpenReport } from 'features/explore/hooks/use-explore-void-open-report';
import { BillboardIcon as BillboardIconDeprecated } from 'components/assets/Icons/BillboardIcon';
import { VenueDisabledIconIcon } from 'components/assets/Icons/VenueDisabledIconIcon';
import { VenueIcon as VenueIconDeprecated } from 'components/assets/Icons/VenueIcon';
import { GisIcon as GisIconDeprecated } from 'components/assets/Icons/GisIcon';
import { CreatePoiIcon as CreatePoiIconDeprecated } from 'components/assets/Icons/CreatePoiIcon';
import { DotsMoreIcon } from 'components/assets/Icons/Icons';
import styles from 'features/explore/shared/components/explore-poi-actions-button/explore-poi-actions-button.module.scss';
import { useExploreFacade } from 'features/explore/model/hooks/use-explore-facade';
import { useFullscreen } from '@advanced-mapping/pl-mapbox';

/*
 * This file contains new hooks that were separated from ExplorePoiActionsButton component and were created as hooks to reuse them.
 * The logic is not changed
 */

export const useOnOpenReport = (poi: IPoi, splunkComponent?: string) => {
    const sendSplunkEvent = useSendExploreSplunkEvent();
    const getOverlay = useGetOverlayPOI();
    const { openReport } = useOpenExploreReportLink();

    const poiData = poi.getPoiData();
    const poiName = poi.name.get();
    const category = poi.categoryDisplayName.get();
    const providerData = poi.getProviderData();
    const entityProviderId = providerData?.entityId.get();
    const isEnclosingPOI = isAnEnclosingComplexFlag(poiData);

    return useCallback(
        (dropdownOption: string, overlayId: string) => {
            if (splunkComponent) {
                sendSplunkEvent({
                    component: splunkComponent,
                    action: 'open report',
                    value: dropdownOption,
                    selectedSource: getPlacePoiProvider(poiData),
                    selectedCategory:
                        category === 'Address' || entityProviderId === COORDINATES_ENTITY
                            ? 'Address-coordinate'
                            : category,
                });
            }
            if (overlayId !== 'customPoi') {
                const selectedOverlay = getSelectedPoiOverlay(getOverlay(poiData), overlayId);

                if (selectedOverlay && isEnclosingPOI) {
                    setEnclosingNote({
                        enclosedPropertyName: poiName,
                        enclosedParentName: selectedOverlay.name,
                    });
                }
                openReport({
                    entity: selectedOverlay ?? poiData,
                    splunkComponentId: splunkComponent,
                });
            }
        },
        [
            splunkComponent,
            sendSplunkEvent,
            poiData,
            category,
            entityProviderId,
            getOverlay,
            isEnclosingPOI,
            openReport,
            poiName,
        ],
    );
};

// See more info on top of this file
const useOpenNearbyActivityReport = (poi: IPoi, splunkComponent?: string) => {
    const setOverlay = useSetOverlayPOI();
    const defaultNearbyActivityRadius = useNearbyActivityRadiusDefault();
    const onOpenReport = useOnOpenReport(poi, splunkComponent);
    const [hasNewOverlay, setHasNewOverlay] = useState(false);

    const poiData = poi.getPoiData();

    // an effect to open the nearby report
    useEffect(() => {
        if (hasNewOverlay) {
            onOpenReport('Nearby Activity', 'nearby');
            setHasNewOverlay(false);
        }
    }, [hasNewOverlay, onOpenReport]);

    return () => {
        setOverlay({
            sourcePoi: poi.getId(),
            overlayPoi: {
                ...createPlaceWithNearbyResult(poiData, defaultNearbyActivityRadius),
                overlayID: 'nearby',
            },
        });
        setHasNewOverlay(true); // will trigger the open-report in the next React cycle
    };
};

const extractPOIProps = (poi: IPoi) => {
    const providerData = poi.getProviderData();
    return {
        poiData: poi.getPoiData(),
        poiId: poi.getId(),
        isMarket: poi.entitySection.get() === 'market',
        isTrafficPin: poi.type.get() === 'billboard',
        isAddressProvider: providerData?.entityId.get() === 'address',
        category: poi.categoryDisplayName.get(),
        providerData,
        entityProviderId: providerData?.entityId.get(),
        provider: providerData?.provider.get(),
        flag: poi.getFlag(),
    };
};

/*
 * This is a refactored code - the logic is not changed and was taken from ExplorePoiActionsButton
 * now ExplorePoiActionsButton is using this hook
 */
export const useDropdownOptions = (poi: IPoi, splunkComponent?: string): DropdownOption[] => {
    const checkPoiForPropertyReport = useCheckPoiForPropertyReport();
    const { customPoiAddresses } = useExploreUIState();
    const {
        poiId,
        poiData,
        category,
        entityProviderId,
        isAddressProvider,
        isMarket,
        isTrafficPin,
    } = extractPOIProps(poi);
    const onOpenReport = useOnOpenReport(
        poi,
        isMarket ? 'explore-report-dropdown' : splunkComponent,
    );
    const onOpenNearbyActivityReport = useOpenNearbyActivityReport(poi, splunkComponent);
    const { isFullscreen } = useFullscreen();
    const getEnclosingComplexes = useGetEnclosingComplexOptions();
    const checkCoordinateForNearbyReport = useCheckCoordinateForNearbyReport();
    const buildVoidAnalysisDropdownOption = useExploreVoidOpenReport();
    const isFreemium = useIsFreemiumUser();
    const onLockedButtonClick = useOnLockedButtonClick();
    const setTriggerCreatePOI = useSetTriggerCreatePOI();
    const exploreFacade = useExploreFacade();

    const { enable_explore_markets_ff, enable_explore_drop_pin_ff } = getFeatureFlags();
    const { unlock_nearby_activity, enable_explore2_dropdown_free_user_ff } = getPermissions();
    const enclosingComplexes = getEnclosingComplexes(poiId);
    const isEnclosingPOI = isAnEnclosingComplexFlag(poiData);
    const isLoadingEnclosingComplexes = isLoadingExploreEnclosingComplexesSignal.value;
    const isLoadingEnclosingPOI = isEnclosingPOI && isLoadingEnclosingComplexes;
    const voidAnalysisButton: DropdownOption = buildVoidAnalysisDropdownOption(
        poiData,
        splunkComponent,
    );

    const { text: propertyTooltip, disabled: propertyDisabled } =
        checkPoiForPropertyReport(poiData);
    const pinTitle = isMarket ? 'Markets' : isTrafficPin ? 'Traffic Pin' : 'Property';
    const { text: nearbyTooltip, disabled: nearbyDisabled } =
        checkCoordinateForNearbyReport(poiData);

    const freemiumWithAvailablePoiAndDropdownExp =
        isFreemium && enable_explore2_dropdown_free_user_ff;
    const lockedOptions =
        !unlock_nearby_activity || freemiumWithAvailablePoiAndDropdownExp
            ? {
                  isLocked: true,
                  tooltip: 'Locked. Premium account is needed',
                  onClick: onLockedButtonClick as () => void,
              }
            : {};

    const shouldHaveCreatePoiOption =
        category === 'Address' ||
        entityProviderId === ADDRESS_ENTITY ||
        customPoiAddresses[entityProviderId || poiId] ||
        (enable_explore_drop_pin_ff && entityProviderId === COORDINATES_ENTITY);

    return [
        {
            key: 'market',
            icon: (
                <span className={styles.marketIcon} data-testid="zip-code-icon">
                    <ZipCodeIcon />
                </span>
            ),
            label: 'Market',
            visible: isMarket,
            disabled: !enable_explore_markets_ff,
            tooltip: enable_explore_markets_ff ? undefined : 'Coming Soon',
            onClick: enable_explore_markets_ff ? () => onOpenReport(pinTitle, 'poi') : undefined,
        },
        {
            key: 'poi',
            icon: isTrafficPin ? (
                <BillboardIconDeprecated />
            ) : propertyDisabled ? (
                <VenueDisabledIconIcon />
            ) : (
                <VenueIconDeprecated overrideInheritedStyle={false} />
            ),
            label: pinTitle,
            visible: !(
                isMarket ||
                ((category === 'Address' || entityProviderId === COORDINATES_ENTITY) &&
                    enable_explore_drop_pin_ff) ||
                isAddressProvider ||
                customPoiAddresses[entityProviderId || poi.getId()]
            ),
            tooltip: propertyTooltip,
            onClick: () => onOpenReport(pinTitle, 'poi'),
            disabled: propertyDisabled,
            useTooltipParentContainer: isFullscreen,
        },
        {
            key: 'enclosing',
            icon: <VenueIconDeprecated overrideInheritedStyle={false} />,
            label: 'Enclosing Property',
            visible: (enclosingComplexes && enclosingComplexes.length > 0) || isLoadingEnclosingPOI,
            disabled: isLoadingEnclosingPOI,
            tooltip: isLoadingEnclosingPOI ? 'Loading enclosing properties' : '',
            onClick: () => onOpenReport('Enclosing Property', 'complex'),
            ...lockedOptions,
        },
        {
            key: 'nearbyActivity',
            icon: <GisIconDeprecated />,
            label: 'Nearby Activity',
            visible: !isMarket && !isTrafficPin,
            tooltip: nearbyTooltip,
            disabled: isMarket || nearbyDisabled,
            onClick: () => onOpenNearbyActivityReport(),
            useTooltipParentContainer: isFullscreen,
            ...lockedOptions,
        },
        {
            ...voidAnalysisButton,
            disabled: isMarket || voidAnalysisButton.disabled,
        },
        {
            key: 'customPoi',
            icon: <CreatePoiIconDeprecated className={styles.createPoiIcon} />,
            label: 'Create POI',
            visible: !!shouldHaveCreatePoiOption,
            tooltip:
                'Draw a specific polygon around this location \n' +
                'to save it for analysis at any time',
            useTooltipParentContainer: isFullscreen,
            onClick: () => {
                setTriggerCreatePOI({ mode: 'create' });
            },
        },
        {
            key: 'moreOptions',
            icon: <DotsMoreIcon className={styles.dotsIconContainer} />,
            label: 'More Options',
            visible: !isTrafficPin,
            onClick: () => {
                exploreFacade.setDrawerPoi(poiData.id, poiData.type);
            },
            ...lockedOptions,
        },
    ];
};

type actionItemsKeys =
    | 'market'
    | 'poi'
    | 'enclosing'
    | 'nearbyActivity'
    | 'voidAnalysis'
    | 'customPoi'
    | 'moreOptions';

const LockedButton = () => {
    const { enable_explore2_dropdown_free_user_ff } = getPermissions();
    const onLockedButtonClick: MouseEventHandler = useOnLockedButtonClick();

    const text = enable_explore2_dropdown_free_user_ff ? 'Report Options' : 'Upgrade Your Plan';

    return (
        <Button
            LeftIcon={<LockIcon />}
            testId="report-options-button-locked"
            onClick={onLockedButtonClick}
            variant="primary-ghost"
            fullWidth
        >
            {text}
        </Button>
    );
};

const getIcon = (poi: IPoi): Record<actionItemsKeys, JSX.Element> => {
    const { isTrafficPin } = extractPOIProps(poi);

    return {
        market: <ZipCodeIcon />,
        poi: isTrafficPin ? <BillboardIcon /> : <VenueIcon />,
        enclosing: <VenueIcon />,
        nearbyActivity: <GisIcon />,
        voidAnalysis: <VoidIcon />,
        customPoi: <PoiIcon />,
        moreOptions: <UtilityMoreIcon />,
    };
};

/*
 * This hook is to create a reusable explore actions options in the form of DS
 */
export const useExplorePoiActions = (poi: IPoi, splunkComponent?: string): PoiActionsProps => {
    const isLockedPoi = useIsLockedPoi();
    const checkCoordinateForNearbyReport = useCheckCoordinateForNearbyReport();
    const isFreemiumUser = useIsFreemiumUser();
    const onOpenReport = useOnOpenReport(poi, splunkComponent);
    const dropdownOptions = useDropdownOptions(poi, splunkComponent);

    const { enable_explore2_dropdown_free_user_ff } = getPermissions();

    const { poiData, flag, isTrafficPin } = extractPOIProps(poi);
    const isFreemiumAvailablePoi = isFreemiumUser && !flag;
    const isEnclosingPOI = isAnEnclosingComplexFlag(poiData);
    const isLoadingEnclosingComplexes = isLoadingExploreEnclosingComplexesSignal.value;
    const isLoadingEnclosingPOI = isEnclosingPOI && isLoadingEnclosingComplexes;
    const { isLoading: isNearbyLoading } = checkCoordinateForNearbyReport(poiData);
    const isLocked = !!isLockedPoi(poiData);

    if (isLocked) {
        return {
            customizedComponent: <LockedButton />,
        };
    }

    // This means that the popover button won't be a dropdown, and it will only act as a property open report
    if (!enable_explore2_dropdown_free_user_ff && isFreemiumAvailablePoi) {
        return {
            text: 'Open Report',
            onClick: () => onOpenReport(isTrafficPin ? 'Traffic Pin' : 'Property', 'poi'),
            items: undefined,
        };
    }

    return {
        text: 'Open Report',
        isLoading: isLoadingEnclosingPOI || isNearbyLoading,
        // Converting old dropdown options to adjust the new DS dropdown
        items: dropdownOptions
            .filter(({ visible }) => visible)
            .map(({ key, tooltip, disabled, label, onClick, isLocked }) => {
                const typedOnClick = onClick as unknown as MouseEventHandler<HTMLDivElement>;
                return {
                    value: key,
                    label,
                    isDisabled: disabled,
                    tooltip,
                    isLeftComponentIcon: true,
                    LeftComponent: getIcon(poi)[key as actionItemsKeys],
                    onClick: disabled ? undefined : typedOnClick,
                    isLocked,
                    onLockedClick: isLocked ? typedOnClick : undefined,
                };
            }),
    };
};
